import React from "react";
import CreatePostModal from "../modals/CreatePostModal";
import DeletePostModal from "../modals/DeletePostModal";
import EditPostModal from "../modals/EditPostModal";
import EnhanceImageModal from "../modals/EnhanceImageModal";
import ImageUploadModal from "../modals/ImageUploadModal";
import MultiPlatformPublishModal from "../modals/MultiPlatformPublishModal";
import PreviewPostModal from "../modals/PreviewPostModal";
import PublishPostModal from "../modals/PublishPostModal";
import RejectPostModal from "../modals/RejectPostModal";
import SchedulePostModal from "../modals/SchedulePostModal";
import SocialVariationsModal from "../modals/SocialVariationsModal";
import UnifiedRepublishModal from "../modals/UnifiedRepublishModal";
import VideoEditorModal from "../modals/VideoEditorModal";
import VideoGenerateModal from "../modals/VideoGenerateModal";
import VideoUploadModal from "../modals/VideoUploadModal";
import BoostLinkedInModal from "../modals/BoostLinkedInModal";
import BoostMetaModal from "../modals/BoostMetaModal";
import BoostGoogleModal from "../modals/BoostGoogleModal";
import CampaignCreativeEditor from "../modals/CampaignCreativeEditor";
import type { UsePostsActionsReturn } from "../../hooks/usePostsActions";
import type { UsePostsAIReturn } from "../../hooks/usePostsAI";
import type { UsePostsMediaReturn } from "../../hooks/usePostsMedia";
import type { UsePostsModalsReturn } from "../../hooks/usePostsModals";
import type { UsePostsPublishingReturn } from "../../hooks/usePostsPublishing";
import { API_BASE_URL, getPostImageUrl } from "../../shared/constants";
import apiService from "../../shared/apiService";
import { showToastError, showToastSuccess } from "../../shared/alertService";
import postsService from "../../services/postsService";

interface PostsModalsProps {
  // Hooks
  modals: UsePostsModalsReturn;
  actions: UsePostsActionsReturn;
  ai: UsePostsAIReturn;
  media: UsePostsMediaReturn;
  publishing: UsePostsPublishingReturn;

  // Estados e funções adicionais
  loading: boolean;
  currentPosts: any[];
  pendingPosts: any[];
  approvedPosts: any[];
  publishedPosts: any[];
  loadPosts: (forceRefresh?: boolean) => Promise<void>;

  // Handlers específicos
  handleReject: (
    postId: number,
    reason: string,
    successMessage?: string,
  ) => Promise<void>;
  handleSchedule: (postId: number, date: string, time: string) => Promise<void>;
  handleEnhancePost: (postId: number) => Promise<void>;
  openVisualSuggestionsModal: (postId: number) => void;
  hasVisualSuggestionsForPostId?: (postId: number) => boolean;
  handleCreatePost: (postData: {
    content: string;
    platforms: string[];
    hashtags?: string;
    call_to_action?: string;
    call_to_action_url?: string;
    imageFile?: File;
    videoFile?: File;
    videoDestination?: string;
    videoCaption?: string;
    videoHashtags?: string;
    generateImage?: boolean;
    imagePrompt?: string;
    imageProvider?: string;
    generateVideo?: boolean;
    videoPrompt?: string;
    videoDuration?: number;
    videoProvider?: string;
    videoRestrictions?: string;
    videoLogoFile?: File | null;
    videoReferenceImages?: File[];
    videoTexts?: import("../../utils/videoGenerationRequest").VideoTextItem[];
    videoScript?: string;
    enhanceWithAI?: boolean;
    tempPostId?: number;
    generatedImageUrl?: string;
  }) => Promise<void>;
}

// Deriva valores iniciais sensatos pro Editor de Criativo a partir de um post
// existente. Antes disso, o "Título" pegava o CTA do post, o "Corpo" pegava
// o conteúdo INTEIRO sem cortar (post virava um parágrafo gigante colado na
// imagem) e o CTA do criativo era sempre um texto genérico fixo, ignorando o
// CTA real do post — resultado: texto errado/desproporcional em toda imagem
// gerada a partir de um post já existente (ex.: vindos do cronograma).
function deriveCreativeEditorDefaults(post: any): {
  header: string;
  body: string;
  cta: string;
} {
  if (!post) return { header: "", body: "", cta: "" };

  const rawContent: string =
    (typeof post.content === "string" ? post.content : post.content?.text) ||
    "";

  // Um post gerado (viral ou cronograma) normalmente já tem a estrutura
  // "hook \n\n corpo \n\n cta \n\n hashtags" — usa o primeiro parágrafo como
  // gancho/título e o segundo como corpo, em vez do texto inteiro.
  const paragraphs = rawContent
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const truncate = (text: string, max: number) =>
    text.length > max ? `${text.slice(0, max - 1).trim()}…` : text;

  const header = truncate(paragraphs[0] || "", 70);
  const body = truncate(paragraphs[1] || paragraphs[0] || "", 150);

  // O `call_to_action` do post costuma ser uma frase inteira (ex.: "Ligue
  // agora ou visite nosso site para..."), útil como CTA de legenda mas não
  // como texto de BOTÃO na imagem — um botão com frase longa fica quebrado/
  // desproporcional. Só reaproveita se já for curto o bastante pra um botão;
  // senão cai no padrão genérico.
  const realCta = (post.call_to_action || "").trim();
  const cta = realCta && realCta.length <= 30 ? realCta : "Saiba mais";

  return { header, body, cta };
}

const PostsModals: React.FC<PostsModalsProps> = ({
  modals,
  actions,
  ai,
  media,
  publishing,
  loading,
  currentPosts,
  pendingPosts,
  approvedPosts,
  publishedPosts,
  loadPosts,
  handleReject,
  handleSchedule,
  handleEnhancePost,
  openVisualSuggestionsModal,
  hasVisualSuggestionsForPostId,
  handleCreatePost,
}) => {
  const creativeEditorDefaults = deriveCreativeEditorDefaults(
    modals.creativeEditorPost,
  );

  return (
    <>
      <RejectPostModal
        open={modals.rejectModalOpen}
        onClose={() => {
          modals.setRejectModalOpen(false);
          modals.setRejectPostId(null);
          modals.setRejectReason("");
        }}
        onConfirm={async () => {
          if (modals.rejectPostId) {
            await handleReject(modals.rejectPostId, modals.rejectReason);
          }
        }}
        loading={loading}
        postId={modals.rejectPostId}
        rejectReason={modals.rejectReason}
        setRejectReason={modals.setRejectReason}
      />

      <EditPostModal
        open={!!modals.editingPost}
        onClose={() => {
          modals.setEditingPost(null);
          modals.setEditContent("");
          modals.setEditHashtags("");
          modals.setEditCallToAction("");
          modals.setEditCallToActionUrl("");
        }}
        onConfirm={() => actions.handleEdit()}
        loading={loading}
        postId={modals.editingPost}
        platform={
          currentPosts.find((p: any) => p.id === modals.editingPost)?.platform
        }
        editContent={modals.editContent}
        setEditContent={modals.setEditContent}
        editHashtags={modals.editHashtags}
        setEditHashtags={modals.setEditHashtags}
        editCallToAction={modals.editCallToAction}
        setEditCallToAction={modals.setEditCallToAction}
        editCallToActionUrl={modals.editCallToActionUrl}
        setEditCallToActionUrl={modals.setEditCallToActionUrl}
        galleryItems={modals.editGalleryItems}
        setGalleryItems={modals.setEditGalleryItems}
        showAIFeatures={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return post?.status === "approved" || post?.status === "scheduled";
          })()
        }
        onEnhanceText={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return post?.status === "approved" || post?.status === "scheduled";
          })()
            ? async () => {
                if (modals.editingPost) {
                  await handleEnhancePost(modals.editingPost);
                  await loadPosts();
                  const updatedPost = currentPosts.find(
                    (p: any) => p.id === modals.editingPost,
                  );
                  if (updatedPost) {
                    modals.setEditContent(updatedPost.content);
                    let hashtagsStr = "";
                    if (
                      Array.isArray(updatedPost.hashtags) &&
                      updatedPost.hashtags.length > 0
                    ) {
                      hashtagsStr = updatedPost.hashtags
                        .map((tag: any) => {
                          const cleanTag = String(tag)
                            .trim()
                            .replace(/^[{#]+/, "")
                            .replace(/}+$/, "");
                          return cleanTag ? `#${cleanTag}` : "";
                        })
                        .filter((tag: string) => tag.length > 0)
                        .join(" ");
                    } else if (
                      typeof updatedPost.hashtags === "string" &&
                      updatedPost.hashtags.trim()
                    ) {
                      const hashtagsString = updatedPost.hashtags.trim();
                      const tags = hashtagsString
                        .split(",")
                        .map((tag: string) => {
                          const cleanTag = tag
                            .trim()
                            .replace(/^[{#]+/, "")
                            .replace(/}+$/, "")
                            .replace(/['"]/g, "");
                          return cleanTag;
                        })
                        .filter((tag: string) => tag.length > 0)
                        .map((tag: string) => `#${tag}`);
                      hashtagsStr = tags.join(" ");
                    }
                    modals.setEditHashtags(hashtagsStr);
                    modals.setEditCallToAction(
                      updatedPost.call_to_action || "",
                    );
                  }
                }
              }
            : undefined
        }
        onUploadImage={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return (
              post?.status === "approved" ||
              post?.status === "scheduled" ||
              post?.status === "pending"
            );
          })()
            ? () => {
                if (modals.editingPost) {
                  modals.setImageModalMode("upload");
                  modals.setShowImageModal(modals.editingPost);
                }
              }
            : undefined
        }
        onUploadVideo={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return (
              post?.status === "approved" ||
              post?.status === "scheduled" ||
              post?.status === "pending"
            );
          })()
            ? () => {
                if (modals.editingPost) {
                  modals.setShowVideoModal(modals.editingPost);
                }
              }
            : undefined
        }
        onGenerateVideo={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return (
              post?.status === "approved" ||
              post?.status === "scheduled" ||
              post?.status === "pending"
            );
          })()
            ? () => {
                if (modals.editingPost) {
                  media.setVideoPrompt("");
                  media.setVideoDuration(8);
                  modals.setShowVideoGenerateModal(modals.editingPost);
                }
              }
            : undefined
        }
        onGenerateImage={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return post?.status === "approved" || post?.status === "scheduled";
          })()
            ? async () => {
                if (modals.editingPost) {
                  modals.setImageModalMode("generate");
                  ai.generateImage(
                    modals.editingPost,
                    !!modals.editContent?.trim(),
                  );
                  await loadPosts();
                }
              }
            : undefined
        }
        onEnhanceImage={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            const hasImage =
              post?.image_info &&
              (post.image_info.filename || post.image_info.public_url);
            return (
              (post?.status === "approved" || post?.status === "scheduled") &&
              hasImage
            );
          })()
            ? () => {
                if (modals.editingPost) {
                  const post = currentPosts.find(
                    (p: any) => p.id === modals.editingPost,
                  );
                  if (post?.image_info) {
                    ai.openEnhanceImageModal(
                      modals.editingPost,
                      getPostImageUrl(post.image_info),
                    );
                  }
                }
              }
            : undefined
        }
        onVisualSuggestions={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return post?.status === "approved" || post?.status === "scheduled";
          })()
            ? () => {
                if (modals.editingPost) {
                  openVisualSuggestionsModal(modals.editingPost);
                }
              }
            : undefined
        }
        hasVisualSuggestions={
          modals.editingPost != null &&
          !!hasVisualSuggestionsForPostId?.(modals.editingPost)
        }
        onCheckGrammar={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return post?.status === "approved" || post?.status === "scheduled";
          })()
            ? async () => {
                if (modals.editingPost) {
                  await ai.checkGrammar(modals.editingPost);
                  await loadPosts();
                  const updatedPost = currentPosts.find(
                    (p: any) => p.id === modals.editingPost,
                  );
                  if (updatedPost) {
                    modals.setEditContent(updatedPost.content);
                  }
                }
              }
            : undefined
        }
        loadingEnhance={
          modals.editingPost !== null
            ? modals.enhancingPost === modals.editingPost
            : false
        }
        loadingImage={
          modals.editingPost !== null
            ? modals.generatingImage === modals.editingPost
            : false
        }
        loadingVideo={
          modals.editingPost !== null
            ? modals.generatingVideo === modals.editingPost ||
              modals.uploadingVideo === modals.editingPost
            : false
        }
        loadingEnhanceImage={
          modals.editingPost !== null
            ? ai.enhancingImage === modals.editingPost
            : false
        }
        loadingGrammar={
          modals.editingPost !== null
            ? ai.checkingGrammar === modals.editingPost
            : false
        }
        onGenerateCreative={
          modals.editingPost !== null &&
          (() => {
            const post = currentPosts.find(
              (p: any) => p.id === modals.editingPost,
            );
            return post?.status === "approved" || post?.status === "scheduled";
          })()
            ? () => {
                if (modals.editingPost) {
                  const post = currentPosts.find(
                    (p: any) => p.id === modals.editingPost,
                  );
                  modals.setCreativeEditorPost(post || null);
                  modals.setShowCreativeEditorModal(modals.editingPost);
                }
              }
            : undefined
        }
      />

      <DeletePostModal
        open={!!modals.showDeleteModal}
        onClose={() => modals.setShowDeleteModal(null)}
        onConfirm={async () => {
          if (modals.showDeleteModal !== null) {
            const postId = modals.showDeleteModal;
            // Se o post for aprovado, usamos o fluxo de "descarte" (status=rejected)
            // em vez de exclusão física, mas mantemos a interface de "excluir" para o usuário
            const isApproved = approvedPosts.some((p: any) => p.id === postId);

            if (isApproved) {
              try {
                await handleReject(
                  postId,
                  "Descartado pelo usuário (Aprovados)",
                  "Post excluído com sucesso!",
                );
                modals.setShowDeleteModal(null);
              } catch (err) {
                // Erro já tratado no handleReject
              }
            } else {
              await actions.handleDelete(postId);
            }
          }
        }}
        loading={loading}
        postId={modals.showDeleteModal}
      />

      <PreviewPostModal
        open={!!modals.showPreviewModal}
        onClose={() => modals.setShowPreviewModal(null)}
        post={
          currentPosts.find((p: any) => p.id === modals.showPreviewModal) ||
          null
        }
        onApprove={
          modals.showPreviewModal !== null
            ? () => {
                const postId = modals.showPreviewModal;
                if (postId !== null) {
                  const post = currentPosts.find((p: any) => p.id === postId);
                  if (post && post.status === "pending") {
                    actions.handleApprove(postId);
                  }
                }
              }
            : undefined
        }
        onEdit={
          modals.showPreviewModal !== null
            ? () => {
                const postId = modals.showPreviewModal;
                if (postId !== null) {
                  const post = currentPosts.find((p: any) => p.id === postId);
                  if (post) {
                    modals.setEditingPost(post.id);
                    modals.setEditContent(post.content);
                    // Converter hashtags
                    let hashtagsStr = "";
                    if (
                      Array.isArray(post.hashtags) &&
                      post.hashtags.length > 0
                    ) {
                      hashtagsStr = post.hashtags
                        .map((tag: any) => {
                          const cleanTag = String(tag)
                            .trim()
                            .replace(/^[{#]+/, "")
                            .replace(/}+$/, "");
                          return cleanTag ? `#${cleanTag}` : "";
                        })
                        .filter((tag: string) => tag.length > 0)
                        .join(" ");
                    } else if (
                      typeof post.hashtags === "string" &&
                      post.hashtags.trim()
                    ) {
                      const hashtagsString = post.hashtags.trim();
                      const tags = hashtagsString
                        .split(",")
                        .map((tag: string) => {
                          const cleanTag = tag
                            .trim()
                            .replace(/^[{#]+/, "")
                            .replace(/}+$/, "")
                            .replace(/['"]/g, "");
                          return cleanTag;
                        })
                        .filter((tag: string) => tag.length > 0)
                        .map((tag: string) => `#${tag}`);
                      hashtagsStr = tags.join(" ");
                    } else if (post.hashtags) {
                      const cleanTag = String(post.hashtags)
                        .trim()
                        .replace(/^[{#]+/, "")
                        .replace(/}+$/, "");
                      hashtagsStr = cleanTag ? `#${cleanTag}` : "";
                    }
                    modals.setEditHashtags(hashtagsStr);
                    modals.setEditCallToAction(post.call_to_action || "");
                    modals.setEditCallToActionUrl(
                      post.call_to_action_url || "",
                    );
                    modals.setShowPreviewModal(null);
                  }
                }
              }
            : undefined
        }
        showActions={true}
      />

      <SchedulePostModal
        open={!!modals.showScheduleModal}
        onClose={() => {
          modals.setShowScheduleModal(null);
          modals.setScheduleDate("");
          modals.setScheduleTime("09:00");
        }}
        onConfirm={() => {
          if (
            modals.showScheduleModal &&
            modals.scheduleDate &&
            modals.scheduleTime
          ) {
            handleSchedule(
              modals.showScheduleModal,
              modals.scheduleDate,
              modals.scheduleTime,
            );
          }
        }}
        loading={modals.schedulingPost === modals.showScheduleModal}
        postId={modals.showScheduleModal}
        scheduleDate={modals.scheduleDate}
        setScheduleDate={modals.setScheduleDate}
        scheduleTime={modals.scheduleTime}
        setScheduleTime={modals.setScheduleTime}
      />

      <PublishPostModal
        open={!!publishing.showPublishModal}
        onClose={() => publishing.setShowPublishModal(null)}
        onConfirm={publishing.handlePublishConfirm}
        loading={modals.publishingPost !== null && loading}
        postId={publishing.showPublishModal}
        publishOptions={publishing.publishOptions}
        selectedOption={publishing.selectedOption}
        setSelectedOption={publishing.setSelectedOption}
      />

      <MultiPlatformPublishModal
        isOpen={!!modals.showMultiPlatformModal}
        onClose={() => {
          modals.setShowMultiPlatformModal(null);
          modals.setSelectedPostForMultiPublish(null);
        }}
        onPublish={publishing.handleMultiPlatformPublish}
        post={modals.selectedPostForMultiPublish}
        loading={modals.publishingPost !== null && loading}
      />

      <UnifiedRepublishModal
        isOpen={modals.showUnifiedRepublishModal}
        onClose={() => {
          modals.setShowUnifiedRepublishModal(false);
          modals.setSelectedPostForRepublish(null);
        }}
        onRepublishLinkedIn={publishing.handleRepublishLinkedIn}
        onPublishMultiPlatform={publishing.handleRepublishMultiPlatform}
        post={modals.selectedPostForRepublish}
      />

      <VideoUploadModal
        open={!!modals.showVideoModal}
        onClose={() => modals.setShowVideoModal(null)}
        onUpload={() =>
          modals.showVideoModal !== null &&
          media.handleUploadVideo(modals.showVideoModal)
        }
        loadingUpload={modals.uploadingVideo === modals.showVideoModal}
        postId={modals.showVideoModal}
        selectedFile={media.selectedVideoFile}
        setSelectedFile={media.setSelectedVideoFile}
        destination={media.videoDestination}
        setDestination={media.setVideoDestination}
        caption={media.videoCaption}
        setCaption={media.setVideoCaption}
      />

      <VideoGenerateModal
        open={!!modals.showVideoGenerateModal}
        onClose={() => {
          modals.setShowVideoGenerateModal(null);
          media.setVideoPrompt("");
          media.setVideoRestrictions("");
          media.setVideoDuration(8);
          media.setLogoFile(null);
          media.setLogoPreview(null);
          media.setReferenceImages([]);
          media.setReferenceImagePreviews([]);
          media.setVideoTexts([]);
          media.setVideoScript("");
        }}
        onGenerate={media.generateVideoWithAI}
        loadingGenerate={
          modals.generatingVideo === modals.showVideoGenerateModal
        }
        videoJobStatus={media.videoJobStatus}
        postId={modals.showVideoGenerateModal}
        videoPrompt={media.videoPrompt}
        setVideoPrompt={media.setVideoPrompt}
        videoDuration={media.videoDuration}
        setVideoDuration={media.setVideoDuration}
        videoRestrictions={media.videoRestrictions}
        setVideoRestrictions={media.setVideoRestrictions}
        videoProvider={media.videoProvider}
        setVideoProvider={media.setVideoProvider}
        logoFile={media.logoFile}
        setLogoFile={media.setLogoFile}
        logoPreview={media.logoPreview}
        setLogoPreview={media.setLogoPreview}
        referenceImages={media.referenceImages}
        setReferenceImages={media.setReferenceImages}
        referenceImagePreviews={media.referenceImagePreviews}
        setReferenceImagePreviews={media.setReferenceImagePreviews}
        videoTexts={media.videoTexts}
        setVideoTexts={media.setVideoTexts}
        videoScript={media.videoScript}
        setVideoScript={media.setVideoScript}
      />

      <ImageUploadModal
        open={!!modals.showImageModal}
        onClose={() => {
          modals.setShowImageModal(null);
          modals.setImageModalPostHasContent(false);
          modals.setImageModalMode("upload");
          media.setSelectedImageFile(null);
          media.setCustomPrompt("");
          media.setImageRestrictions("");
          media.setImageProvider("dalle");
          media.setImageLogoFile(null);
          media.setImageLogoPreview(null);
          media.setImageReferenceImages([]);
          media.setImageReferenceImagePreviews([]);
        }}
        onUpload={media.handleUploadImage}
        onGenerate={media.handleGenerateImage}
        loadingUpload={modals.uploadingImage === modals.showImageModal}
        loadingGenerate={modals.generatingImage === modals.showImageModal}
        imageJobStatus={media.imageJobStatus}
        postId={modals.showImageModal}
        selectedFile={media.selectedImageFile}
        setSelectedFile={media.setSelectedImageFile}
        customPrompt={media.customPrompt}
        setCustomPrompt={media.setCustomPrompt}
        imageRestrictions={media.imageRestrictions}
        setImageRestrictions={media.setImageRestrictions}
        imageProvider={media.imageProvider}
        setImageProvider={media.setImageProvider}
        logoFile={media.imageLogoFile}
        setLogoFile={media.setImageLogoFile}
        logoPreview={media.imageLogoPreview}
        setLogoPreview={media.setImageLogoPreview}
        referenceImages={media.imageReferenceImages}
        setReferenceImages={media.setImageReferenceImages}
        referenceImagePreviews={media.imageReferenceImagePreviews}
        setReferenceImagePreviews={media.setImageReferenceImagePreviews}
        postHasContent={modals.imageModalPostHasContent}
        mode={modals.imageModalMode}
      />

      <EnhanceImageModal
        open={!!modals.showEnhanceImageModal}
        onClose={() => {
          modals.setShowEnhanceImageModal(null);
          modals.setEnhanceImageImageUrl("");
        }}
        postId={modals.showEnhanceImageModal}
        imageUrl={modals.enhanceImageImageUrl}
        onConfirm={ai.handleConfirmEnhanceImage}
        loading={
          modals.showEnhanceImageModal !== null &&
          ai.enhancingImage === modals.showEnhanceImageModal
        }
      />

      <CreatePostModal
        open={modals.showCreatePostModal}
        onClose={() => modals.setShowCreatePostModal(false)}
        onCreate={handleCreatePost}
        loading={modals.creatingPost}
        onCreativeGenerated={() => loadPosts(true)}
      />

      <VideoEditorModal
        open={!!modals.showVideoEditorModal}
        onClose={() => modals.setShowVideoEditorModal(null)}
        onSave={
          modals.showVideoEditorModal !== null
            ? async (data) => {
                await media.handleSaveVideoEdits(
                  modals.showVideoEditorModal || 0,
                  data,
                );
              }
            : async () => {}
        }
        postId={modals.showVideoEditorModal || 0}
        videoDuration={8}
        initialTexts={(() => {
          const allPosts = [
            ...pendingPosts,
            ...approvedPosts,
            ...publishedPosts,
          ];
          const post = allPosts.find(
            (p: any) => p.id === modals.showVideoEditorModal,
          );
          return (
            (post?.video_info as any)?.texts ||
            (post?.video_info as any)?.video_texts ||
            []
          );
        })()}
        initialScript={(() => {
          const allPosts = [
            ...pendingPosts,
            ...approvedPosts,
            ...publishedPosts,
          ];
          const post = allPosts.find(
            (p: any) => p.id === modals.showVideoEditorModal,
          );
          return (post?.video_info as any)?.script || "";
        })()}
        loading={media.editingVideo === modals.showVideoEditorModal}
        videoUrl={(() => {
          const allPosts = [
            ...pendingPosts,
            ...approvedPosts,
            ...publishedPosts,
          ];
          const post = allPosts.find(
            (p: any) => p.id === modals.showVideoEditorModal,
          );
          if (post?.video_info) {
            const videoInfo = post.video_info as any;
            if (videoInfo.public_url) {
              return videoInfo.public_url.startsWith("http")
                ? videoInfo.public_url
                : `${API_BASE_URL.replace("/api", "")}${videoInfo.public_url}`;
            } else if (videoInfo.filename) {
              return `${API_BASE_URL.replace("/api", "")}/api/videos/${videoInfo.filename}`;
            }
          }
          return undefined;
        })()}
        videoFilename={(() => {
          const allPosts = [
            ...pendingPosts,
            ...approvedPosts,
            ...publishedPosts,
          ];
          const post = allPosts.find(
            (p: any) => p.id === modals.showVideoEditorModal,
          );
          return (post?.video_info as any)?.filename;
        })()}
      />

      <SocialVariationsModal
        open={modals.showSocialVariationsModal}
        onClose={() => {
          modals.setShowSocialVariationsModal(false);
          modals.setSelectedVariations(null);
          modals.setSelectedOriginalUrl("");
          modals.setSelectedPostForVariations(null);
        }}
        variations={modals.selectedVariations || {}}
        originalUrl={modals.selectedOriginalUrl}
        post={modals.selectedPostForVariations}
        onDuplicate={async (platform) => {
          try {
            const originalPost = modals.selectedPostForVariations;
            if (!originalPost) return;

            // 1. Chamar duplicação no backend
            const result = await postsService.duplicatePost(
              originalPost.id,
              platform,
            );

            if (result && result.id) {
              // 2. Fechar o modal de variações
              modals.setShowSocialVariationsModal(false);

              // 3. Abrir o modal de agendamento para o NOVO post
              setTimeout(() => {
                modals.setShowScheduleModal(result.id);
                showToastSuccess(
                  `Post duplicado para ${platform}. Escolha a data para agendar.`,
                );
                // Recarregar lista para mostrar o novo post (pendente/aprovado)
                loadPosts();
              }, 300);
            }
          } catch (err: any) {
            showToastError(err.message || "Erro ao duplicar post");
          }
        }}
      />

      <BoostMetaModal
        open={!!modals.showBoostMetaModal}
        onClose={() => {
          modals.setShowBoostMetaModal(null);
          modals.setSelectedPostForBoost(null);
        }}
        onConfirm={async (data) => {
          if (modals.showBoostMetaModal && modals.selectedPostForBoost) {
            modals.setBoostingPost(modals.showBoostMetaModal);
            try {
              // Reutilizar o logic de boost já existente mas com dados do modal
              const body = {
                ...data,
                platform: modals.selectedPostForBoost.platform || "facebook",
                name: `Boost Post #${modals.showBoostMetaModal}`,
                approved_post_id: modals.showBoostMetaModal,
                start_paused: true,
              };

              const result = await apiService.post("/ads/meta/campaigns", body);

              if (result.success) {
                modals.setShowBoostMetaModal(null);
                showToastSuccess(
                  "Publicação turbinada com sucesso! A Meta analisará seu anúncio em breve.",
                );
              } else {
                throw new Error(result.error || "Erro ao turbinar");
              }
            } catch (err: any) {
              // Error handled by modal
              throw err;
            } finally {
              modals.setBoostingPost(null);
            }
          }
        }}
        loading={modals.boostingPost !== null}
        post={modals.selectedPostForBoost}
      />

      <BoostGoogleModal
        open={
          !!modals.showBoostMetaModal &&
          modals.selectedPostForBoost?.platform === "google"
        }
        onClose={() => {
          modals.setShowBoostMetaModal(null);
          modals.setSelectedPostForBoost(null);
        }}
        onConfirm={async (data) => {
          if (modals.showBoostMetaModal && modals.selectedPostForBoost) {
            modals.setBoostingPost(modals.showBoostMetaModal);
            try {
              const body = {
                ...data,
                name: `Boost Post #${modals.showBoostMetaModal}`,
                approved_post_id: modals.showBoostMetaModal,
                link_url:
                  modals.selectedPostForBoost.link_url ||
                  "https://virtusautomation.com",
              };

              const result = await apiService.post(
                "/ads/google/campaigns",
                body,
              );

              if (result.success) {
                modals.setShowBoostMetaModal(null);
                showToastSuccess("Publicação turbinada no Google com sucesso!");
              } else {
                throw new Error(result.error || "Erro ao turbinar no Google");
              }
            } catch (err: any) {
              throw err;
            } finally {
              modals.setBoostingPost(null);
            }
          }
        }}
        loading={modals.boostingPost !== null}
        post={modals.selectedPostForBoost}
      />
      <BoostLinkedInModal
        open={!!modals.showBoostLinkedInModal}
        onClose={() => {
          modals.setShowBoostLinkedInModal(null);
          modals.setSelectedPostForBoost(null);
        }}
        onConfirm={async (data) => {
          if (modals.showBoostLinkedInModal && modals.selectedPostForBoost) {
            modals.setBoostingPost(modals.showBoostLinkedInModal);
            try {
              // Endpoint unificado de ads
              const result = await apiService.post("/ads/campaigns", data);

              if (result.id) {
                modals.setShowBoostLinkedInModal(null);
                showToastSuccess(
                  "Publicação patrocinada no LinkedIn com sucesso!",
                );
              } else {
                throw new Error("Falha ao criar campanha no LinkedIn");
              }
            } catch (err: any) {
              console.error("Erro no boost LinkedIn:", err);
              throw err;
            } finally {
              modals.setBoostingPost(null);
            }
          }
        }}
        loading={modals.boostingPost !== null}
        post={modals.selectedPostForBoost}
      />

      <CampaignCreativeEditor
        open={!!modals.showCreativeEditorModal}
        onClose={() => {
          modals.setShowCreativeEditorModal(null);
          modals.setCreativeEditorPost(null);
        }}
        postId={modals.showCreativeEditorModal}
        initialHeader={creativeEditorDefaults.header}
        initialBody={creativeEditorDefaults.body}
        initialCta={creativeEditorDefaults.cta}
        initialRawBackgroundUrl={
          modals.creativeEditorPost?.image_info?.raw_background_url || null
        }
        onCreativeGenerated={() => loadPosts(true)}
      />
    </>
  );
};

export default PostsModals;
